import * as Sentry from "@sentry/react";
import { trackEvent } from "../analytics/analytics";
import apiClient, { apiUrl } from "../../services/apiClient";
import { getAuthToken } from "../accounts/auth.service";
import { journeyManager } from "../guide/journeyManager";

export const getFileBlob = async (url: string) => {
  const response = await fetch(url);
  return await response.blob();
};

export const getDataBlob = async (url: string, data) => {
  const response = await apiClient.post(url, data, {
    //responseType: "blob",
  });
  return response.data as Blob;
};

export const fetchFileType = async (url: string, name: string) => {
  const e = name.lastIndexOf(".");
  if (e > 0) return name.substring(e + 1).toUpperCase();
  const blob = await getFileBlob(url);
  const type = blob?.type?.toUpperCase() ?? "";
  const i = type.lastIndexOf("/");
  return i > 0 ? type.substring(i + 1) : type;
};

export const downloadFile = async (url: string, name: string, data?) => {
  const blob = data ? await getDataBlob(url, data) : await getFileBlob(url);
  const link = document.createElement("a");
  link.style.display = "none";
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.parentNode.removeChild(link);
  }, 0);
};

// Drops to KB under a megabyte: these strings go in front of the user, and a 161 KB
// image reported as "0.2 MB" reads like a bug in the message rather than a size.
const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`;

// CM2-2QN: the binding ceiling is the transport, not the importer. Neither
// /data/csv-import nor /images/upload carries a [RequestSizeLimit], so both inherit
// Kestrel's default 30,000,000-byte cap, and Kestrel aborts the connection mid-send
// when a body crosses it. The abort is not an HTTP response, so it carries no CORS
// headers and the browser reports only `status 0` — which means the importer's own
// polite ceiling (SpreadsheetExtractor.MaxCsvImportBytes, 25 MB) can never be
// reached, let alone spoken. Measured: a 63.7 MB .xlsx burned 16s and then told the
// user to check a connection that was never down. Refuse here, where the size is
// known before a byte is sent. Sized to the importer's 25 MB rather than the
// transport's 28.6 MB so multipart framing and the option fields still fit.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const uploadFile = (
  service: "image" | "csv-import",
  file,
  path: string,
  options,
  onload: (request) => void,
  onprogress?,
  abort?
) =>
  new Promise((_resolve, reject) => {
    // CM2-2HG: callers pass a file held in component state (e.g. csvFile.file in
    // ImportCsvModal), which can be cleared or absent by the time the button is
    // clicked. FormData.append then throws a raw TypeError that surfaces as an
    // unhandled rejection with no user-facing message. Fail with something the
    // user can act on instead.
    if (!(file instanceof Blob)) {
      reject(new Error("No file selected. Please choose a file and try again."));
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      reject(
        new Error(
          `This file is ${formatSize(file.size)}, over the ${formatSize(
            MAX_UPLOAD_BYTES
          )} upload limit. Please split it or save a smaller version.`
        )
      );
      return;
    }

    trackEvent({
      category: service,
      action: "upload",
      label: path,
      feature: true,
      assets: service == "image" ? ["space"] : undefined,
    });
    journeyManager.updateJourneyProgress("discovery", "image_upload");

    // create a formdata object to send to the server
    // Trim: stray whitespace in filenames produces blob names that Cloudflare
    // canonicalizes away, breaking the catalog renderer. Server also trims, but
    // catch it here so progress UI / FormData reflect the cleaned name.
    const formData = new FormData();
    formData.append("file", file, path?.trim());

    if (options) {
      for (const key in options) {
        formData.append(key, options[key]);
      }
    }

    const serviceUrl =
      service === "image" ? "/images/upload" : "/data/csv-import";

    // create a request object
    const request = new XMLHttpRequest();
    request.open("POST", apiUrl + serviceUrl);
    request.setRequestHeader("Authorization", `Bearer ${getAuthToken()}`);

    // show progress in interface
    // bytesSent is what separates "never left the browser" from "died in flight" in
    // onerror below, so it is tracked even when no caller wants progress.
    let bytesSent = 0;
    request.upload.onprogress = (e) => {
      bytesSent = e.loaded;
      onprogress?.(e);
    };

    // catch errors
    // CM2-2QN: `onerror` carries no status, so it cannot name its own cause — and the
    // one message it used to throw ("check your connection") was wrong for every cause
    // measured, grouping all of them under one Sentry fingerprint. navigator.onLine and
    // bytesSent discriminate the three real cases, which now fingerprint separately:
    // offline, nothing-ever-sent, and interrupted-in-flight.
    const startedAt = Date.now();
    request.onerror = () => {
      const elapsedMs = Date.now() - startedAt;
      Sentry.addBreadcrumb({
        category: "upload",
        level: "error",
        message: "xhr onerror",
        data: {
          service,
          sizeKb: Math.round(file.size / 1024),
          elapsedMs,
          online: navigator.onLine,
          bytesSent,
        },
      });
      if (!navigator.onLine) {
        reject(
          new Error("You appear to be offline. Check your connection and try again.")
        );
      } else if (bytesSent === 0) {
        // Not one byte left the browser while the network was up: the request was
        // refused before it started, and a stale file handle is what does that.
        // Measured: a 161 KB image failed in 53ms while six siblings in the same
        // FilePond batch returned 200.
        reject(
          new Error(
            "This file could not be read from your device. Please choose it again."
          )
        );
      } else if (bytesSent >= file.size) {
        // The whole body went out and nothing came back: the request outlived the edge,
        // not the connection. This is the 2026-10-06 shape — account 332097's CSV held
        // raw base64 in an image column, the import ran 111-125s, and Cloudflare cut it
        // at ~100s. The server may well have finished, so "try again" is the one thing
        // not to advise: a blind retry double-imports.
        reject(
          new Error(
            "The file uploaded, but the server did not answer in time. It may still have " +
              "gone through — check your products before trying again."
          )
        );
      } else {
        reject(
          new Error(
            `The upload stopped after ${formatSize(bytesSent)} of ${formatSize(
              file.size
            )}. Please try again.`
          )
        );
      }
    };
    request.ontimeout = () => reject(new Error("Upload timed out. Check your connection and try again."));

    // handle success state
    request.onload = () => onload(request);

    // CM2-2QN: a File from Android's photo picker (and any file moved or re-saved after
    // it was chosen) can go stale between selection and send; XHR then dies with
    // ERR_UPLOAD_FILE_CHANGED before sending a byte. `file.size` is metadata cached at
    // selection time and still reads fine, so only a real read detects this — one byte
    // is enough, and it costs nothing next to the upload itself.
    const head = file.slice(0, 1);
    if (typeof head.arrayBuffer !== "function") {
      // Pre-Blob.arrayBuffer browser: skip the probe rather than block the upload.
      request.send(formData);
    } else {
      head.arrayBuffer().then(
        () => request.send(formData),
        () =>
          reject(
            new Error(
              "This file is no longer available on your device. Please choose it again."
            )
          )
      );
    }

    return {
      abort: () => {
        // This function is entered if the user has tapped the cancel button
        request.abort();

        // Let FilePond know the request has been cancelled
        abort?.();
      },
    };
  });
